import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Lock, Mail, User, Sparkles, Shield, Eye, EyeOff, Loader2 } from 'lucide-react';

const LOGO_URL = `${process.env.PUBLIC_URL}/logo.png`;

export default function LoginPage() {
  const { login, register, requestMagicLink, verifyMagicLink, checkInvite } = useAuth();
  
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicToken, setMagicToken] = useState('');
  
  // Form states
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ email: '', password: '', fullName: '' });
  const [magicEmail, setMagicEmail] = useState('');
  
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setErrors({});
    
    if (!validateEmail(loginForm.email)) {
      setErrors({ email: 'Please enter a valid email' });
      return;
    }
    
    if (!loginForm.password) {
      setErrors({ password: 'Password is required' });
      return;
    }
    
    setLoading(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Welcome back!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setErrors({});
    
    if (!registerForm.fullName.trim()) {
      setErrors({ fullName: 'Full name is required' });
      return;
    }
    
    if (!validateEmail(registerForm.email)) {
      setErrors({ email: 'Please enter a valid email' });
      return;
    }
    
    if (registerForm.password.length < 8) {
      setErrors({ password: 'Password must be at least 8 characters' });
      return;
    }
    
    setLoading(true);
    try {
      // First check if email is invited
      const inviteCheck = await checkInvite(registerForm.email);
      if (!inviteCheck.is_invited) {
        toast.error('Access restricted to invited investors only.');
        setLoading(false);
        return;
      }
      
      await register(registerForm.email, registerForm.password, registerForm.fullName);
      toast.success('Account created successfully!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setErrors({});
    
    if (!validateEmail(magicEmail)) {
      setErrors({ magicEmail: 'Please enter a valid email' });
      return;
    }
    
    setLoading(true);
    try {
      const response = await requestMagicLink(magicEmail);
      setMagicLinkSent(true);
      // For demo purposes, auto-fill the token
      if (response.demo_token) {
        setMagicToken(response.demo_token);
      }
      toast.success('Magic link sent to your email!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send magic link.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMagicLink = async (e) => {
    e.preventDefault();
    
    if (!magicToken.trim()) {
      toast.error('Please enter the magic link token');
      return;
    }
    
    setLoading(true);
    try {
      await verifyMagicLink(magicToken);
      toast.success('Successfully logged in!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid or expired magic link.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary-light via-background to-background flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-primary/5 rounded-full blur-3xl" />
      </div>
      
      <div className="relative w-full max-w-md">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src={LOGO_URL} alt="Physiomobile" className="h-16 w-auto" />
          </div>
          <h1 className="text-2xl font-display font-bold text-foreground mb-2">
            Investor Portal
          </h1>
          <p className="text-sm text-muted-foreground">
            Secure access for invited Pre-Series A investors only
          </p>
        </div>
        
        {/* Login Card */}
        <Card className="border shadow-xl">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2 text-primary mb-2">
              <Shield className="h-5 w-5" />
              <span className="text-sm font-medium">Private Access</span>
            </div>
            <CardTitle className="text-xl">Welcome</CardTitle>
            <CardDescription>
              Sign in to access the investor dashboard
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login">Sign In</TabsTrigger>
                <TabsTrigger value="register">Register</TabsTrigger>
              </TabsList>
              
              {/* Login Tab */}
              <TabsContent value="login" className="space-y-4">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="investor@example.com"
                        value={loginForm.email}
                        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                        className={`pl-10 ${errors.email ? 'border-destructive' : ''}`}
                      />
                    </div>
                    {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={loginForm.password}
                        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                        className={`pl-10 pr-10 ${errors.password ? 'border-destructive' : ''}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
                  </div>
                  
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>
                
                {/* Magic Link Section */}
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                  </div>
                </div>
                
                {!magicLinkSent ? (
                  <form onSubmit={handleMagicLink} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="magic-email">Magic Link</Label>
                      <div className="relative">
                        <Sparkles className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="magic-email"
                          type="email"
                          placeholder="Enter your email"
                          value={magicEmail}
                          onChange={(e) => setMagicEmail(e.target.value)}
                          className={`pl-10 ${errors.magicEmail ? 'border-destructive' : ''}`}
                        />
                      </div>
                      {errors.magicEmail && <p className="text-xs text-destructive">{errors.magicEmail}</p>}
                    </div>
                    
                    <Button type="submit" variant="outline" className="w-full" disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Sparkles className="mr-2 h-4 w-4" />
                          Send Magic Link
                        </>
                      )}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleVerifyMagicLink} className="space-y-4">
                    <div className="p-3 rounded-lg bg-success-light border border-success/20 text-sm">
                      <p className="text-success font-medium">Magic link sent!</p>
                      <p className="text-muted-foreground text-xs mt-1">
                        Check your email and enter the token below (demo: token auto-filled)
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="magic-token">Magic Token</Label>
                      <Input
                        id="magic-token"
                        type="text"
                        placeholder="Enter magic token"
                        value={magicToken}
                        onChange={(e) => setMagicToken(e.target.value)}
                      />
                    </div>
                    
                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Verifying...
                        </>
                      ) : (
                        'Verify & Sign In'
                      )}
                    </Button>
                    
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full"
                      onClick={() => {
                        setMagicLinkSent(false);
                        setMagicToken('');
                      }}
                    >
                      Try a different email
                    </Button>
                  </form>
                )}
              </TabsContent>
              
              {/* Register Tab */}
              <TabsContent value="register" className="space-y-4">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="p-3 rounded-lg bg-warning-light border border-warning/20 text-sm">
                    <p className="text-warning font-medium">Invitation Required</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Only pre-approved investor emails can register
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="Enter your full name"
                        value={registerForm.fullName}
                        onChange={(e) => setRegisterForm({ ...registerForm, fullName: e.target.value })}
                        className={`pl-10 ${errors.fullName ? 'border-destructive' : ''}`}
                      />
                    </div>
                    {errors.fullName && <p className="text-xs text-destructive">{errors.fullName}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="investor@example.com"
                        value={registerForm.email}
                        onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                        className={`pl-10 ${errors.email ? 'border-destructive' : ''}`}
                      />
                    </div>
                    {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="register-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Create a password (min 8 chars)"
                        value={registerForm.password}
                        onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                        className={`pl-10 pr-10 ${errors.password ? 'border-destructive' : ''}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
                  </div>
                  
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        {/* Demo credentials */}
        <div className="mt-6 p-4 rounded-lg bg-muted/50 border border-border">
          <p className="text-xs text-muted-foreground text-center mb-2">
            <strong>Demo Credentials:</strong>
          </p>
          <div className="text-xs text-muted-foreground text-center space-y-1">
            <p>Email: <code className="bg-muted px-1 rounded">investor@test.com</code></p>
            <p>Password: <code className="bg-muted px-1 rounded">Test1234!</code></p>
            <p className="text-[10px] mt-2">Or use magic link with any invited email</p>
          </div>
        </div>
        
        {/* Footer */}
        <p className="text-xs text-muted-foreground text-center mt-6">
          Protected by enterprise-grade security
        </p>
      </div>
    </div>
  );
}
